/**
 * Admin-only: the history and near future of team-position synthesis.
 *
 * `POSITION_SET_AT` records the last pass that wrote something. It cannot
 * distinguish a pass that failed, a pass that found nothing to write, and a
 * pass that never ran — which is exactly the question an old position raises.
 * This shows the passes themselves.
 */
import { AlertTriangle, CheckCircle2, Clock, Loader2, XCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useSynthesisQueue } from "@/lib/useAdmin";
import { cn } from "@/lib/utils";
import type { SynthesisRun } from "@/lib/types";

const TRIGGER_LABEL: Record<string, string> = {
  response_change: "automatic",
  admin_recompute: "manual",
  override_cleared: "pin removed",
};

function ago(value: string | null): string {
  if (!value) return "—";
  const mins = Math.round((Date.now() - new Date(value).getTime()) / 60000);
  if (Number.isNaN(mins)) return "—";
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  return hours < 24 ? `${hours}h ago` : `${Math.round(hours / 24)}d ago`;
}

function inWords(dueAt: string): string {
  const secs = Math.round((new Date(dueAt).getTime() - Date.now()) / 1000);
  if (Number.isNaN(secs)) return "soon";
  if (secs <= 0) return "any moment";
  return secs < 60 ? `in ${secs}s` : `in ${Math.round(secs / 60)}m`;
}

function RunRow({ run }: { run: SynthesisRun }) {
  const running = run.status === "running";
  const failed = run.status === "failed";
  return (
    <div className="flex items-start justify-between gap-3 border-t border-bw-blue-muted/30 py-2">
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="flex items-center gap-1.5 font-sans text-sm text-bw-ink">
          {running && <Loader2 className="h-3.5 w-3.5 animate-spin text-bw-blue" aria-hidden />}
          {failed && <XCircle className="h-3.5 w-3.5 text-bw-navy" aria-hidden />}
          {!running && !failed && <CheckCircle2 className="h-3.5 w-3.5 text-bw-blue" aria-hidden />}
          {run.topic_title}
          <span className="font-normal text-bw-ink-muted">
            · {TRIGGER_LABEL[run.trigger] ?? run.trigger}
            {run.triggered_by && ` by ${run.triggered_by}`}
          </span>
        </span>
        <span className="font-sans text-xs text-bw-ink-muted">
          started {ago(run.started_at)}
          {run.status === "succeeded" && (
            <>
              {" "}
              · wrote {run.positions_written ?? 0} position
              {run.positions_written === 1 ? "" : "s"} from {run.responses_considered ?? 0} take
              {run.responses_considered === 1 ? "" : "s"}
            </>
          )}
          {/* A pass that started long ago and never reported back didn't
              "succeed quietly" — it died. Say which. */}
          {running && <> · still running</>}
        </span>
        {failed && run.error && (
          <span className="truncate font-sans text-xs text-bw-navy" title={run.error}>
            {run.error}
          </span>
        )}
      </div>
      <span className={cn("shrink-0 font-sans text-xs", failed ? "text-bw-navy" : "text-bw-ink-muted")}>
        {run.status}
      </span>
    </div>
  );
}

export function SynthesisQueue() {
  const { data, isLoading, isError } = useSynthesisQueue(true);
  const runs = data?.runs ?? [];
  const pending = data?.pending ?? [];

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Synthesis queue</CardTitle>
        <p className="font-sans text-xs text-bw-ink-muted">
          Every pass that rebuilt the team positions — automatic, manual, or
          triggered by removing a pin — plus anything the scheduler is about to run.
        </p>
      </CardHeader>
      <CardContent className="pt-0">
        {isLoading && <p className="font-sans text-sm text-bw-ink-muted">Loading…</p>}
        {isError && <p className="font-sans text-sm text-bw-ink-muted">Couldn&apos;t load the queue.</p>}

        {!isLoading && !isError && (
          <div className="flex flex-col gap-1">
            <span className="flex items-center gap-1.5 font-sans text-xs font-semibold uppercase tracking-wide text-bw-ink-muted">
              <Clock className="h-3.5 w-3.5" aria-hidden />
              Next up
            </span>
            {pending.length === 0 ? (
              <p className="font-sans text-sm text-bw-ink-muted">Nothing scheduled.</p>
            ) : (
              pending.map((p) => (
                <p key={p.topic_key} className="font-sans text-sm text-bw-ink">
                  {p.topic_title}{" "}
                  <span className="text-bw-ink-muted">· {inWords(p.due_at)}</span>
                </p>
              ))
            )}
            <p className="flex items-start gap-2 pt-1 font-sans text-xs text-bw-ink-muted">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-bw-blue" aria-hidden />
              <span>
                Scheduled passes live in the running process. A restart drops them
                without a trace, so an empty list right after a deploy means the
                queue was lost, not that nothing was queued — recompute the topic
                by hand if a position looks behind.
              </span>
            </p>
          </div>
        )}

        {runs.length > 0 && (
          <div className="mt-4 flex flex-col">
            <span className="font-sans text-xs font-semibold uppercase tracking-wide text-bw-ink-muted">
              Recent passes
            </span>
            {runs.map((run) => (
              <RunRow key={run.run_id} run={run} />
            ))}
          </div>
        )}
        {!isLoading && !isError && runs.length === 0 && (
          <p className="mt-4 font-sans text-sm text-bw-ink-muted">No passes recorded yet.</p>
        )}
      </CardContent>
    </Card>
  );
}
